import { DialogContent, Modal, IconButton, Dialog } from '@mui/material';
import { Close } from '@mui/icons-material';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import GooglePlayStoreg from "../rsc/playstore.png"
import { useTranslation } from 'react-i18next';
import { Organisation } from '../models/organisation/OrganisationModelV2';
import { ProfileModel } from '../models/person/ProfileModel';
import { buildAppTheme } from '../theme';
import { Theme } from '@mui/material/styles';

export interface AppContextData {
    profile: ProfileModel
    setProfile: (profile: ProfileModel) => void
    setDisplayAndroidPopup: any
    millSelected: Organisation
    setMillSelected: (org: Organisation) => void
    theme: string
    setTheme: (theme: string) => void
    muiTheme: Theme
}

export const AppContext = createContext<AppContextData>(undefined);

export const REDIRECT_URL = "https://poapp.pempem.org/applinks?destination=app&referrer=dest%3Dprice_map%26origin%3Dwelcome"

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppContextProvider');
    }
    return context;
};

interface AppContextProviderProps {
    children: React.ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {

    const [isAndroidPopupShown, setDisplayAndroidPopup] = useState<boolean>(false)
    const [profile, setProfile] = useState<ProfileModel>(undefined)
    const [millSelected, setMillSelected] = useState<Organisation>(undefined)
    const [theme, setTheme] = useState<string>(localStorage.getItem('theme') ?? 'light')
    const { t } = useTranslation()

    // Sync dark-mode class on html + body + persist to localStorage.
    // html needs it too: it is the element whose background paints the canvas,
    // so without it any area outside the body box falls back to the light theme.
    // `dark` is the selector @pempem-org/components ships its dark styles under,
    // so it has to go on alongside our own `theme-dark`.
    useEffect(() => {
        localStorage.setItem('theme', theme)
        if (theme === 'dark') {
            document.documentElement.classList.add('theme-dark', 'dark')
            document.body.classList.add('theme-dark', 'dark')
        } else {
            document.documentElement.classList.remove('theme-dark', 'dark')
            document.body.classList.remove('theme-dark', 'dark')
        }
    }, [theme])

    // Build MUI theme reactively when mode changes
    const muiTheme = useMemo(
        () => buildAppTheme(theme === 'dark' ? 'dark' : 'light'),
        [theme]
    )

    return (
        <AppContext.Provider
            value={{
                setDisplayAndroidPopup: setDisplayAndroidPopup,
                profile: profile,
                setProfile,
                millSelected,
                setMillSelected,
                theme,
                setTheme,
                muiTheme,
            }}>
            {children}
            <Dialog
                onClose={() => setDisplayAndroidPopup(false)}
                open={isAndroidPopupShown}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogContent style={{ outline: 'none', width: '400px' }} className='pempem-flex-row-center'>
                    <div style={{ background: 'white', alignItems: 'center', gap: 15, borderRadius: '12px', position: 'relative' }} className='pempem-flex-col'>
                        <h3 style={{ margin: 0, textAlign: 'center', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                            {t('mobile_experience_title')}
                        </h3>
                        <p style={{ margin: 0, textAlign: 'center', fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
                            {t('mobile_experience_description')}
                        </p>
                        <a href={REDIRECT_URL} style={{ textDecoration: 'none', width: '200px' }}>
                            <div
                                className='pempem-flex-row'
                                style={{
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    height: "60px",
                                    borderRadius: "8px",
                                    alignItems: "center"
                                }} >
                                <img alt="background" style={{ height: "50px" }} src={GooglePlayStoreg} />
                            </div>
                        </a>
                    </div>
                    <IconButton
                        onClick={() => setDisplayAndroidPopup(false)}
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            color: '#999'
                        }}
                        size="small">
                        <Close />
                    </IconButton>
                </DialogContent>
            </Dialog>
        </AppContext.Provider >
    );
};
